export enum ServiceRequestStatus {
  // Request phase
  created = 'created',
  clientApproached = 'client approached',
  internalReviewRequested = 'internal review requested',
  moreInformationRequested = 'more information requested',
  reviewCompleted = 'review completed',
  priceQuoteReady = 'price quote ready',

  // Payment phase
  paymentLinkSent = 'payment link sent',
  orderCompleted = 'order completed',
  paid = 'paid',

  // Delivery phase
  processed = 'processed',

  // Final states
  dismissed = 'dismissed',
  refunded = 'refunded',
}

export const serviceRequestNotDeliveredStatuses = [
  ServiceRequestStatus.dismissed,
  ServiceRequestStatus.refunded,
]

export const serviceRequestFinalStatuses = [
  ServiceRequestStatus.processed,
  ...serviceRequestNotDeliveredStatuses,
]

export enum ServiceRequestActions {
  checkoutCompleted = 'checkout completed',
  notPaidReminder = 'not paid reminder sent',
  documentsRequested = 'documents requested',
  documentsPageVisited = 'documents page visited',
}

export enum LegalServices {
  tmStrategy = 'tm-strategy',
  extraChecksPackage1 = 'extra-checks-package-1',
  extraChecksPackage2 = 'extra-checks-package-2',
  tmRepresentation = 'tm-representation',
  tmAssignment = 'tm-assignment',
  gsLimitation = 'tm-limitation',
  responseToObjection = 'tm-objection',
  coexistenceAgreement = 'tm-coexistence',
  cdLetter = 'tm-c&d',
  tmSettlement = 'tm-settlement',
  enforcementOfRights = 'enforcement-of-rights',
  declarationOfIncontestability = 'tm-declaration-of-incontestability',
  officeActionDeadlineExtension = 'tm-office-action-deadline-extension',
  infringementTakedown = 'infringement-takedown',
}

export enum ExtraServices {
  extraClasses = 'extra-classes',
  nonStandardCountryRegistration = 'non-standard-country-registration',
  customGSList = 'custom-gs-list',
  e2eProtection = 'e2e-protection',
}

export enum OtherServices {
  b2bPartnership = 'b2b-partnership',
  domainResolution = 'domain-resolution',
  tmWatch = 'tm-watch',
  brandMonitoring = 'brand-monitoring',
}

// Maybe not entirely necessary but as we play around with pricing I define them as separate services from normal c&d and opposition
export enum ProblemSolutionServices {
  monitoringCaD = 'tm-monitoring-c&d-letter',
  monitoringOpposition = 'tm-monitoring-opposition',
  administrativeChange = 'admin-change',
  declarationOfUse = 'tm-declaration-of-use',
  warningLetter = 'tm-monitoring-warning-letter',
  monitoringSocialMediaCaD = 'tm-monitoring-social-media-c&d-letter',
  monitoringMarketplaceCaD = 'tm-monitoring-marketplace-c&d-letter',
  tmRegistration = 'tm-registration',
}

export enum UnpaidServices {
  demandLetter = 'demand-letter',
  importTrademarks = 'import-trademarks',
}

export enum CommonServices {
  tmAssignmentAgreement = 'tm-assignment-agreement',
  statementOfUse = 'tm-statement-of-use',
  declarationOfUse = 'tm-declaration-of-use',
  officeAction = 'tm-office-action',
  filingOpposition = 'filing-opposition',
  tmRenewal = 'tm-renewal',
  cdLetterResponse = 'tm-c&d-response',
  cdLetter = 'tm-c&d',
  caseSolution = 'case-solution',
}

export type ProofOfUseService = CommonServices.statementOfUse | CommonServices.declarationOfUse

// different set of labels internally than what is shown to user
export enum ServiceRequestInternalType {
  EUIPCase = 'eu-ip-case',
  UKIPCase = 'uk-ip-case',
  USIPCase = 'us-ip-case',
  otherIPCase = 'other-ip-case',
  IPCaseThroughPartner = 'ip-case-through-partner',

  USDiffOfficeAction = 'us-diff-office-action',
  EUDiffOfficeAction = 'eu-diff-office-action',
  UKDiffOfficeAction = 'uk-diff-office-action',
  AUNZDiffOfficeAction = 'aunz-diff-office-action',
  otherDiffOfficeAction = 'other-diff-office-action',
  diffOfficeActionForPartner = 'diff-office-action-for-partner',
  diffOfficeActionThroughPartner = 'diff-office-action-through-partner',
  USEasyOfficeAction = 'us-easy-office-action',
  EUEasyOfficeAction = 'eu-easy-office-action',
  UKEasyOfficeAction = 'uk-easy-office-action',
  AUNZEasyOfficeAction = 'aunz-easy-office-action',
  otherEasyOfficeAction = 'other-easy-office-action',
  easyOfficeActionForPartner = 'easy-office-action-for-partner',
  easyOfficeActionThroughPartner = 'easy-office-action-through-partner',

  USStatementOfUse = 'us-statement-of-use',
  SoUForPartner = 'statement-of-use-for-partner',
  USDeclarationOfUse = 'us-declaration-of-use',
  DoUForPartner = 'declaration-of-use-for-partner',

  otherEasyFollowUp = 'other-easy-follow-up',
  caseAssessment = 'case-assessment',
  tmRenewal = 'tm-renewal',
  tmAssignment = 'tm-assignment',
  tmRepresentation = 'tm-representation',
  EUMonitoring = 'eu-monitoring',
  UKMonitoring = 'uk-monitoring',
  tmConsultation = 'tm-consultation',
  tmStrategy = 'tm-strategy',
  extraChecksPackage1 = 'extra-checks-package-1',
  extraChecksPackage2 = 'extra-checks-package-2',
  extraClasses = 'extra-classes',
  customGSList = 'custom-gs-list',
  e2eProtection = 'e2e-protection',
  tmWatch = 'tm-watch',
  domainResolution = 'domain-resolution',
  b2bPartnership = 'b2b-partnership',
  nonStandardCountryRegistration = 'non-standard-country-registration',
  euipoGrant = 'euipo-grant',

  caseSolution = 'case-solution',
  infringementTakedown = 'infringement-takedown',
  brandMonitoring = 'brand-monitoring',

  // DEPRECATED
  USOfficeActionDeadlineExtension = 'us-office-action-deadline-extension',
  SKCZIPCase = 'skcz-ip-case',
  USOfficeAction = 'us-office-action',
  EUOfficeAction = 'eu-office-action',
  UKOfficeAction = 'uk-office-action',
  AUOfficeAction = 'au-office-action',
  SKCZOfficeAction = 'skcz-office-action',
  CNOfficeAction = 'cn-office-action',
  USLikelihoodOfConfusion = 'us-loc',
  otherOfficeAction = 'other-office-action',
}

export const CustomerService = {...CommonServices, ...LegalServices, ...OtherServices}
export const Service = {...CustomerService, ...ExtraServices, ...ProblemSolutionServices}
export type CustomerService = CommonServices | LegalServices | OtherServices
export type Service = CustomerService | ExtraServices | ProblemSolutionServices

export const ServicesWithAdministrativeFee: Array<Service> = [
  Service.extraClasses,
  Service.tmRepresentation,
  Service.tmAssignment,
  Service.tmRenewal,
  Service.statementOfUse,
  Service.declarationOfUse,
  Service.gsLimitation,
  Service.declarationOfIncontestability,
  Service.monitoringOpposition,
]

export enum LegalServiceGroups {
  simple = 'simple',
  complex = 'complex',
}

export enum LegalServiceCategory {
  registration = 'registration',
  postFiling = 'post-filing',
  maintenance = 'maintenance',
  infringement = 'infringement',
}

export type LegalServiceContent = {
  key: string
  title: string
  description?: string
  linkUrl?: string
  group?: LegalServiceGroups
  priceNote?: string
  hasCustomPage: boolean
  processDetail?: {
    category: LegalServiceCategory
    title?: string
    description: string
    buttonText: string
  }
}
